import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Container } from "reactstrap";
import MetaData from "components/VerticalLayout/MetaData";
import AwaitingPreparationFilter from "pages/meter-management/awaitingPreparationMeters/AwaitingPreparationFilter";
import { bulkPrepareMeters, retrieveMeters } from "utils/api";
import { defaultNgiErrorMethod, successMessage, warnMessage } from "components/Common/responses/message";
import NGITable from "components/Common/NGITable";
import { FILTER_PAGE } from "utils/constants";
import useStoreFilters from "customHooks/useStoreFilters";
import { awaitingPreparationMeterListColumns } from "pages/meter-management/awaitingPreparationMeters/awaitingPreparationMeterListColumns";
import ConfirmationModal from "components/Common/modals/ConfirmationModal";
import CustomButton from "components/Common/buttons/CustomButton";
import approvalIcon from "assets/images/svg/approval_img.svg";
import BulkMeterValidationModal from "pages/meter-management/awaitingPreparationMeters/bulkMeterValidationModal";
import NGITooltip from "components/Common/utils/NGITooltip";
import wrenchIcon from "assets/images/svg/Wrench.svg";

const AwaitingPreparationMeters = () => {
  const [isLoading, setLoader] = useState(false);
  const { setFilters } = useStoreFilters(FILTER_PAGE.AWAITING_PREPARATION);
  const [meterList, setMeterList] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [confirmPrepareId, setConfirmPrepareId] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [isBulkOpen, setIsBulkOpen] = useState(false);

  useEffect(() => {
    handleFilter();
  }, []);

  const closeBulk = useCallback(() => setIsBulkOpen(false), []);
  const openBulk = useCallback(() => setIsBulkOpen(true), []);

  const handleFilter = useCallback((newFilterOptions = {}) => {
    setLoader(true);
    setFilters(newFilterOptions);
    retrieveMeters({
      condition: "Awaiting Preparation",
      ...newFilterOptions,
    })
      .then(res => {
        const {
          result: { items },
        } = res?.data;
        if (items.length === 0) {
          warnMessage("No meters were found");
          setLoader(false);
        }
        setMeterList(res?.data);
        setLoader(false);
      })
      .catch(err => {
        setLoader(false);
        setMeterList(null);
        defaultNgiErrorMethod(err);
      });
  }, []);

  const handleConfirm = useCallback(
    ids => {
      setLoader(true);
      bulkPrepareMeters({ ids })
        .then(() => {
          setLoader(false);
          successMessage("Meter validation process was successfully started.");
          setSelectedRowKeys([]);
          closeConfirmationModal();
          handleFilter();
        })
        .catch(e => {
          defaultNgiErrorMethod(e);
          closeConfirmationModal();
          setLoader(false);
        });
    },
    [confirmPrepareId]
  );

  const openConfirmationModal = useCallback(id => {
    setConfirmPrepareId(id);
    setConfirmationOpen(true);
  }, []);

  const closeConfirmationModal = useCallback(() => {
    setConfirmPrepareId(null);
    setConfirmationOpen(false);
  }, []);

  const rowSelection = {
    selectedRowKeys,
    onChange: newKeys => {
      if (newKeys.length > 10) {
        setSelectedRowKeys(newKeys.slice(0, 10));
      } else {
        setSelectedRowKeys(newKeys);
      }
    },
    renderCell: (checked, record, index, node) => {
      if (selectedRowKeys.length === 10 && !checked) {
        return <NGITooltip title="Only 10 items are allowed.">{node}</NGITooltip>;
      }
      return node;
    },
    getCheckboxProps: record => ({
      disabled: selectedRowKeys.length >= 10 && !selectedRowKeys.includes(record.id),
    }),
    onSelectAll: isSelect => {
      if (!isSelect) {
        setSelectedRowKeys([]);
      } else {
        const {
          result: { items },
        } = meterList;
        setSelectedRowKeys(items.slice(0, 10).map(row => row.id));
      }
    },
  };

  const columns = useMemo(() => awaitingPreparationMeterListColumns(openConfirmationModal), [openConfirmationModal]);

  return (
    <>
      <div className="page-content">
        <MetaData pageTitle={"Awaiting Preparation"} />
        <Container fluid>
          <div className="filters-wrapper">
            <div className="filter-buttons">
              <CustomButton iconImg={wrenchIcon} onClick={openBulk} type="primary" text="BULK VALIDATE" />
              {!!selectedRowKeys.length && (
                <CustomButton
                  onClick={() => openConfirmationModal(null)}
                  isButtonRound
                  iconImg={approvalIcon}
                  type="primary"
                />
              )}
            </div>

            <AwaitingPreparationFilter handleFilter={handleFilter} isLoading={isLoading} />
          </div>
          <Col lg={12}>
            <NGITable
              rowSelection={rowSelection}
              rowKey={record => record.id}
              filtersType={FILTER_PAGE.AWAITING_PREPARATION}
              isLoading={isLoading}
              columns={columns}
              data={meterList}
              updateList={handleFilter}
            />
          </Col>
        </Container>
        {!!confirmationOpen && (
          <ConfirmationModal
            description="Are you sure you want to validate the selected meter(s)?"
            onCancel={closeConfirmationModal}
            onConfirm={() => {
              handleConfirm(confirmPrepareId ? [confirmPrepareId] : selectedRowKeys);
            }}
            confirmBtnTitle="CONFIRM"
            cancelBtnTitle="CANCEL"
          />
        )}
        {isBulkOpen && <BulkMeterValidationModal onClose={closeBulk} />}
      </div>
    </>
  );
};

export default AwaitingPreparationMeters;
